import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import clsx from 'clsx';

interface HomeApp {
  id: string;
  name: string;
  description: string | null;
  url: string;
  icon_type: string;
  icon_value: string;
  category: string;
  bg_color: string;
  text_color: string;
  sort_order: number;
  visible: boolean;
  requires_auth: boolean;
  required_permission: string | null;
  open_in: string;
  audience: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '/api';


// API helper
async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  if (response.status === 204) return null;
  return response.json();
}

// Settings Page
export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<'pricing' | 'leadtimes' | 'homeportal' | 'dealerhome' | 'pto' | 'rewards' | 'discipline' | 'training' | 'banner' | 'promotions'>('pricing');
  const [loading, setLoading] = useState(true);

  // ACH Discount Settings
  const [achDiscountPercent, setAchDiscountPercent] = useState<number>(0);
  const [authorizeAchDiscountPercent, setAuthorizeAchDiscountPercent] = useState<number>(0);
  const [helcimAchDiscountPercent, setHelcimAchDiscountPercent] = useState<number>(0);
  const [achLoading, setAchLoading] = useState(false);
  const [achSaving, setAchSaving] = useState(false);
  const [achSuccess, setAchSuccess] = useState('');
  const [achError, setAchError] = useState('');

  // Price Increase Settings
  const [priceIncreasePercent, setPriceIncreasePercent] = useState<number>(0);
  const [priceIncreaseReason, setPriceIncreaseReason] = useState('');
  const [priceIncreaseMeta, setPriceIncreaseMeta] = useState<{ reason?: string; changedAt?: string; changedBy?: string }>({});
  const [tariffPercent, setTariffPercent] = useState<number>(0);
  const [tariffReason, setTariffReason] = useState('');
  const [tariffMeta, setTariffMeta] = useState<{ reason?: string; changedAt?: string; changedBy?: string }>({});
  const [openRollDiscount, setOpenRollDiscount] = useState<number>(4);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceSaving, setPriceSaving] = useState(false);
  const [priceSuccess, setPriceSuccess] = useState('');
  const [priceError, setPriceError] = useState('');

  // Rep Firm Pricing
  const [repFirms, setRepFirms] = useState<any[]>([]);
  const [repFirmSaving, setRepFirmSaving] = useState<string | null>(null);

  // Add Rep Firm modal
  const [showAddRepFirm, setShowAddRepFirm] = useState(false);
  const [territories, setTerritories] = useState<any[]>([]);
  const [addRepFirmSaving, setAddRepFirmSaving] = useState(false);
  const [addRepFirmError, setAddRepFirmError] = useState('');
  const [addRepFirmSuccess, setAddRepFirmSuccess] = useState('');
  const [newRepFirm, setNewRepFirm] = useState({
    name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    website: '',
    price_adjustment_percent: 2,
    contract_notes: '',
    territory_id: '',
    // Primary contact (for rep portal login) — optional
    primary_first_name: '',
    primary_last_name: '',
    primary_email: '',
    primary_phone: '',
  });

  // Lead Time Settings
  const [leadTimes, setLeadTimes] = useState({
    solar: 10,
    premiumSolar: 12,
    cvw: 10,
    insect: 10,
  });
  const [leadTimeLoading, setLeadTimeLoading] = useState(false);
  const [leadTimeSaving, setLeadTimeSaving] = useState(false);
  const [leadTimeSuccess, setLeadTimeSuccess] = useState('');
  const [leadTimeError, setLeadTimeError] = useState('');

  // Shipping Markup Settings
  const [shippingMarkup, setShippingMarkup] = useState<number>(15);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingSaving, setShippingSaving] = useState(false);
  const [shippingSuccess, setShippingSuccess] = useState('');
  const [shippingError, setShippingError] = useState('');

  // Custom Powder Coating Settings
  const [powderCoatCharge, setPowderCoatCharge] = useState<number>(495);
  const [powderCoatLoading, setPowderCoatLoading] = useState(false);
  const [powderCoatSaving, setPowderCoatSaving] = useState(false);
  const [powderCoatSuccess, setPowderCoatSuccess] = useState('');
  const [powderCoatError, setPowderCoatError] = useState('');

  // Custom Component Color Charge Settings
  const [componentColorCharge, setComponentColorCharge] = useState<number>(245);
  const [componentColorLoading, setComponentColorLoading] = useState(false);
  const [componentColorSaving, setComponentColorSaving] = useState(false);
  const [componentColorSuccess, setComponentColorSuccess] = useState('');
  const [componentColorError, setComponentColorError] = useState('');

  // PTO Settings
  const [ptoVacationDays, setPtoVacationDays] = useState(10);
  const [ptoVacationAccrual, setPtoVacationAccrual] = useState(0.5);
  const [ptoSickDays, setPtoSickDays] = useState(5);
  const [ptoSickAccrual, setPtoSickAccrual] = useState(0.25);
  const [ptoPersonalDays, setPtoPersonalDays] = useState(3);
  const [ptoPersonalAccrual, setPtoPersonalAccrual] = useState(0.125);
  const [ptoHoursPerDay, setPtoHoursPerDay] = useState(8);
  const [ptoLoading, setPtoLoading] = useState(false);
  const [ptoSaving, setPtoSaving] = useState(false);
  const [ptoSuccess, setPtoSuccess] = useState('');
  const [ptoError, setPtoError] = useState('');

  // Employee Rewards Settings
  const [rewardStepPoints, setRewardStepPoints] = useState(5);
  const [rewardSilver, setRewardSilver] = useState(2000);
  const [rewardGold, setRewardGold] = useState(5000);
  const [rewardPlatinum, setRewardPlatinum] = useState(10000);
  const [rewardLoading, setRewardLoading] = useState(false);
  const [rewardSaving, setRewardSaving] = useState(false);
  const [rewardSuccess, setRewardSuccess] = useState('');
  const [rewardError, setRewardError] = useState('');

  // Discipline Settings
  const [discMaxInfractions, setDiscMaxInfractions] = useState(100);
  const [discPointsEnabled, setDiscPointsEnabled] = useState(true);
  const [discDefaultPoints, setDiscDefaultPoints] = useState(50);
  const [discLoading, setDiscLoading] = useState(false);
  const [discSaving, setDiscSaving] = useState(false);
  const [discSuccess, setDiscSuccess] = useState('');
  const [discError, setDiscError] = useState('');

  // Training Settings
  const [trainingPassScore, setTrainingPassScore] = useState(80);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingSaving, setTrainingSaving] = useState(false);
  const [trainingSuccess, setTrainingSuccess] = useState('');
  const [trainingError, setTrainingError] = useState('');

  // CC Surcharge Settings
  const [ccSurchargeEnabled, setCcSurchargeEnabled] = useState(false);
  const [ccSurchargePercent, setCcSurchargePercent] = useState(3);
  const [ccSurchargeStates, setCcSurchargeStates] = useState<string[]>(['CT', 'MA', 'PR']);
  const [ccSurchargeSaving, setCcSurchargeSaving] = useState(false);
  const [ccSurchargeSuccess, setCcSurchargeSuccess] = useState('');
  const [ccSurchargeError, setCcSurchargeError] = useState('');

  // Weight Bar Kit Settings
  const [wbkPrice, setWbkPrice] = useState(99);
  const [wbkThreshold, setWbkThreshold] = useState(72);
  const [wbkSaving, setWbkSaving] = useState(false);
  const [wbkSuccess, setWbkSuccess] = useState('');
  const [wbkError, setWbkError] = useState('');

  const loadAchSettings = async () => {
    setAchLoading(true);
    try {
      const data = await apiRequest('/payment/ach-settings');
      // Use ?? not || — a real 0% must not fall through to the 2% default.
      setAchDiscountPercent(data.achDiscountPercent ?? 0);
      setAuthorizeAchDiscountPercent(data.authorizeAchDiscountPercent ?? 0);
      setHelcimAchDiscountPercent(data.helcimAchDiscountPercent ?? 0);
    } catch (err) {
      console.error('Failed to load ACH settings:', err);
    } finally {
      setAchLoading(false);
    }
  };

  const loadPricingSettings = async () => {
    setPriceLoading(true);
    try {
      const data = await apiRequest('/system-settings/pricing');
      setPriceIncreasePercent(data.price_increase_percent || 0);
      setTariffPercent(data.tariff_percent || 0);
      setOpenRollDiscount(data.open_roll_discount_percent ?? 4);
      setPriceIncreaseMeta({ reason: data.price_increase_reason, changedAt: data.price_increase_changed_at, changedBy: data.price_increase_changed_by });
      setTariffMeta({ reason: data.tariff_reason, changedAt: data.tariff_changed_at, changedBy: data.tariff_changed_by });
      setCcSurchargeEnabled(data.cc_surcharge_enabled === true);
      setCcSurchargePercent(Number(data.cc_surcharge_percent ?? 3));
      if (Array.isArray(data.cc_surcharge_state_blocklist)) {
        setCcSurchargeStates(data.cc_surcharge_state_blocklist);
      }
      setWbkPrice(Number(data.weight_bar_kit_price ?? 99));
      setWbkThreshold(Number(data.weight_bar_kit_threshold_inches ?? 72));
    } catch (err) {
      console.error('Failed to load pricing settings:', err);
    } finally {
      setPriceLoading(false);
    }
  };

  const saveWeightBarKit = async () => {
    setWbkSaving(true);
    setWbkError('');
    setWbkSuccess('');
    try {
      const resp = await apiRequest('/system-settings/weight-bar-kit', {
        method: 'POST',
        body: JSON.stringify({ price: wbkPrice, thresholdInches: wbkThreshold }),
      });
      if (resp?.error) { setWbkError(resp.error); return; }
      setWbkSuccess(`Saved — $${wbkPrice}/kit auto-adds for screens under ${wbkThreshold}" wide.`);
    } catch (err: any) {
      setWbkError(err?.message || 'Failed to save');
    } finally {
      setWbkSaving(false);
    }
  };

  const saveCcSurchargeSettings = async () => {
    setCcSurchargeSaving(true);
    setCcSurchargeError('');
    setCcSurchargeSuccess('');
    try {
      const resp = await apiRequest('/system-settings/cc-surcharge', {
        method: 'POST',
        body: JSON.stringify({ enabled: ccSurchargeEnabled, percent: ccSurchargePercent }),
      });
      if (resp?.error) {
        setCcSurchargeError(resp.error);
        return;
      }
      setCcSurchargeSuccess(
        ccSurchargeEnabled
          ? `CC surcharge ENABLED at ${ccSurchargePercent}%. Card payments now include a processing fee. Debit auto-refunded.`
          : 'CC surcharge disabled. No fees added to card payments.',
      );
    } catch (err: any) {
      setCcSurchargeError(err?.message || 'Failed to save');
    } finally {
      setCcSurchargeSaving(false);
    }
  };

  const loadRepFirms = async () => {
    try {
      const data = await apiRequest('/rep-firms');
      setRepFirms(data || []);
    } catch (err) { console.error('Failed to load rep firms:', err); }
  };

  const loadTerritories = async () => {
    try {
      const data = await apiRequest('/territories');
      setTerritories(Array.isArray(data) ? data : (data?.data || []));
    } catch (err) { console.error('Failed to load territories:', err); }
  };

  const openAddRepFirm = () => {
    setNewRepFirm({
      name: '', contact_name: '', contact_email: '', contact_phone: '',
      street: '', city: '', state: '', zip: '', website: '',
      price_adjustment_percent: 2, contract_notes: '', territory_id: '',
      primary_first_name: '', primary_last_name: '', primary_email: '', primary_phone: '',
    });
    setAddRepFirmError('');
    setAddRepFirmSuccess('');
    loadTerritories();
    setShowAddRepFirm(true);
  };

  const submitAddRepFirm = async () => {
    setAddRepFirmError('');
    setAddRepFirmSuccess('');
    if (!newRepFirm.name.trim()) { setAddRepFirmError('Firm name is required'); return; }
    setAddRepFirmSaving(true);
    try {
      // 1) Create firm
      const firm = await apiRequest('/rep-firms', {
        method: 'POST',
        body: JSON.stringify({
          name: newRepFirm.name.trim(),
          contact_name: newRepFirm.contact_name.trim() || undefined,
          contact_email: newRepFirm.contact_email.trim() || undefined,
          contact_phone: newRepFirm.contact_phone.trim() || undefined,
          address: (newRepFirm.street || newRepFirm.city || newRepFirm.state || newRepFirm.zip || newRepFirm.website) ? {
            street: newRepFirm.street.trim() || undefined,
            city: newRepFirm.city.trim() || undefined,
            state: newRepFirm.state.trim() || undefined,
            zip: newRepFirm.zip.trim() || undefined,
            website: newRepFirm.website.trim() || undefined,
          } : undefined,
          contract_notes: newRepFirm.contract_notes.trim() || undefined,
        }),
      });
      const firmId = firm?.id;
      if (!firmId) throw new Error('Firm created but no ID returned');

      // 2) Set price_adjustment_percent (default = 2% Kim override) — separate PATCH
      if (newRepFirm.price_adjustment_percent !== 0) {
        await apiRequest(`/rep-firms/${firmId}`, {
          method: 'PATCH',
          body: JSON.stringify({ price_adjustment_percent: newRepFirm.price_adjustment_percent }),
        });
      }

      // 3) Assign territory (optional)
      if (newRepFirm.territory_id) {
        try {
          await apiRequest(`/rep-firms/${firmId}/territories`, {
            method: 'POST',
            body: JSON.stringify({ territory_id: newRepFirm.territory_id, effective_date: new Date().toISOString().slice(0, 10) }),
          });
        } catch (err: any) {
          console.warn('Territory assign failed (non-fatal):', err.message);
        }
      }

      // 4) Create primary contact (optional — fires welcome email)
      let portalNote = '';
      if (newRepFirm.primary_email.trim()) {
        try {
          const contact = await apiRequest(`/rep-firms/${firmId}/contacts`, {
            method: 'POST',
            body: JSON.stringify({
              email: newRepFirm.primary_email.trim(),
              firstName: newRepFirm.primary_first_name.trim() || undefined,
              lastName: newRepFirm.primary_last_name.trim() || undefined,
              phone: newRepFirm.primary_phone.trim() || undefined,
              role: 'admin',
              isPrimary: true,
            }),
          });
          portalNote = ` Welcome email sent to ${newRepFirm.primary_email.trim()} with temp password.`;
          // The temp password is in `contact.password` — show it once for the admin to record.
          if (contact?.password) {
            portalNote += ` Temp password: ${contact.password}`;
          }
        } catch (err: any) {
          portalNote = ` ⚠ Firm created but primary contact failed: ${err.message}`;
        }
      }

      setAddRepFirmSuccess(`Rep firm "${newRepFirm.name.trim()}" created.${portalNote}`);
      await loadRepFirms();
      // Auto-close after 4 seconds unless there's a password to copy
      if (!portalNote.includes('Temp password')) {
        setTimeout(() => setShowAddRepFirm(false), 2500);
      }
    } catch (err: any) {
      setAddRepFirmError(err?.message || 'Failed to create rep firm');
    } finally {
      setAddRepFirmSaving(false);
    }
  };

  const saveRepFirmPricing = async (firmId: string, value: number) => {
    setRepFirmSaving(firmId);
    try {
      await apiRequest(`/rep-firms/${firmId}`, {
        method: 'PATCH',
        body: JSON.stringify({ price_adjustment_percent: value }),
      });
      await loadRepFirms();
    } catch (err: any) {
      alert(err.message || 'Failed to save');
    } finally {
      setRepFirmSaving(null);
    }
  };

  const loadLeadTimes = async () => {
    setLeadTimeLoading(true);
    try {
      const data = await apiRequest('/system-settings/lead-times');
      setLeadTimes({
        solar: data.solar ?? 10,
        premiumSolar: data.premiumSolar ?? 12,
        cvw: data.cvw ?? 10,
        insect: data.insect ?? 10,
      });
    } catch (err) {
      console.error('Failed to load lead times:', err);
    } finally {
      setLeadTimeLoading(false);
    }
  };

  const loadShippingMarkup = async () => {
    setShippingLoading(true);
    try {
      const data = await apiRequest('/freight/shipping-markup');
      setShippingMarkup(data.shippingMarkupPercent || 15);
    } catch (err) {
      console.error('Failed to load shipping markup:', err);
    } finally {
      setShippingLoading(false);
    }
  };

  const loadPowderCoatCharge = async () => {
    setPowderCoatLoading(true);
    try {
      const data = await apiRequest('/system-settings/powder-coat-charge');
      setPowderCoatCharge(data.powder_coat_charge || 495);
    } catch (err) {
      console.error('Failed to load powder coat charge:', err);
    } finally {
      setPowderCoatLoading(false);
    }
  };

  const saveLeadTimes = async () => {
    setLeadTimeSaving(true);
    setLeadTimeError('');
    setLeadTimeSuccess('');
    try {
      await apiRequest('/system-settings/lead-times', {
        method: 'POST',
        body: JSON.stringify(leadTimes),
      });
      setLeadTimeSuccess('Lead times updated successfully');
      setTimeout(() => setLeadTimeSuccess(''), 3000);
    } catch (err: any) {
      setLeadTimeError(err.message || 'Failed to save lead times');
    } finally {
      setLeadTimeSaving(false);
    }
  };

  const saveShippingMarkup = async () => {
    setShippingSaving(true);
    setShippingError('');
    setShippingSuccess('');
    try {
      await apiRequest('/freight/shipping-markup', {
        method: 'PATCH',
        body: JSON.stringify({ percent: shippingMarkup }),
      });
      setShippingSuccess('Shipping markup updated successfully');
      setTimeout(() => setShippingSuccess(''), 3000);
    } catch (err: any) {
      setShippingError(err.message || 'Failed to save shipping markup');
    } finally {
      setShippingSaving(false);
    }
  };

  const savePowderCoatCharge = async () => {
    setPowderCoatSaving(true);
    setPowderCoatError('');
    setPowderCoatSuccess('');
    try {
      await apiRequest('/system-settings/powder-coat-charge', {
        method: 'POST',
        body: JSON.stringify({ value: powderCoatCharge }),
      });
      setPowderCoatSuccess('Powder coat charge updated successfully');
      setTimeout(() => setPowderCoatSuccess(''), 3000);
    } catch (err: any) {
      setPowderCoatError(err.message || 'Failed to save powder coat charge');
    } finally {
      setPowderCoatSaving(false);
    }
  };

  const loadComponentColorCharge = async () => {
    setComponentColorLoading(true);
    try {
      const data = await apiRequest('/system-settings/component-color-charge');
      setComponentColorCharge(data.component_color_charge || 245);
    } catch (err) {
      console.error('Failed to load component color charge:', err);
    } finally {
      setComponentColorLoading(false);
    }
  };

  const saveComponentColorCharge = async () => {
    setComponentColorSaving(true);
    setComponentColorError('');
    setComponentColorSuccess('');
    try {
      await apiRequest('/system-settings/component-color-charge', {
        method: 'POST',
        body: JSON.stringify({ value: componentColorCharge }),
      });
      setComponentColorSuccess('Component color charge updated successfully');
      setTimeout(() => setComponentColorSuccess(''), 3000);
    } catch (err: any) {
      setComponentColorError(err.message || 'Failed to save component color charge');
    } finally {
      setComponentColorSaving(false);
    }
  };

  const savePriceIncrease = async () => {
    setPriceSaving(true);
    setPriceError('');
    setPriceSuccess('');
    try {
      await apiRequest('/system-settings/price-increase', {
        method: 'POST',
        body: JSON.stringify({ value: priceIncreasePercent, reason: priceIncreaseReason || undefined }),
      });
      await apiRequest('/system-settings/tariff', {
        method: 'POST',
        body: JSON.stringify({ value: tariffPercent, reason: tariffReason || undefined }),
      });
      await apiRequest('/system-settings/open-roll-discount', {
        method: 'POST',
        body: JSON.stringify({ value: openRollDiscount }),
      });
      setPriceSuccess('Pricing settings updated successfully');
      setTimeout(() => setPriceSuccess(''), 3000);
    } catch (err: any) {
      setPriceError(err.message || 'Failed to save price increase');
    } finally {
      setPriceSaving(false);
    }
  };

  const saveAchSettings = async () => {
    setAchSaving(true);
    setAchError('');
    setAchSuccess('');
    try {
      await apiRequest('/payment/ach-settings', {
        method: 'POST',
        body: JSON.stringify({ achDiscountPercent }),
      });
      await apiRequest('/system-settings/authorize-ach-discount', {
        method: 'POST',
        body: JSON.stringify({ value: authorizeAchDiscountPercent }),
      });
      await apiRequest('/system-settings/helcim-ach-discount', {
        method: 'POST',
        body: JSON.stringify({ value: helcimAchDiscountPercent }),
      });
      setAchSuccess('ACH discount settings updated successfully');
      setTimeout(() => setAchSuccess(''), 3000);
    } catch (err: any) {
      setAchError(err.message || 'Failed to save ACH settings');
    } finally {
      setAchSaving(false);
    }
  };

  // PTO load/save
  const loadPtoSettings = async () => {
    setPtoLoading(true);
    try {
      const data = await apiRequest('/system-settings/pto');
      setPtoVacationDays(data.pto_vacation_days ?? 10);
      setPtoVacationAccrual(data.pto_vacation_accrual_rate ?? 0.5);
      setPtoSickDays(data.pto_sick_days ?? 5);
      setPtoSickAccrual(data.pto_sick_accrual_rate ?? 0.25);
      setPtoPersonalDays(data.pto_personal_days ?? 3);
      setPtoPersonalAccrual(data.pto_personal_accrual_rate ?? 0.125);
      setPtoHoursPerDay(data.pto_hours_per_day ?? 8);
    } catch (err) { console.error('Failed to load PTO settings:', err); }
    finally { setPtoLoading(false); }
  };

  const savePtoSettings = async () => {
    setPtoSaving(true); setPtoError(''); setPtoSuccess('');
    try {
      await apiRequest('/system-settings/pto', {
        method: 'POST',
        body: JSON.stringify({
          pto_vacation_days: ptoVacationDays,
          pto_vacation_accrual_rate: ptoVacationAccrual,
          pto_sick_days: ptoSickDays,
          pto_sick_accrual_rate: ptoSickAccrual,
          pto_personal_days: ptoPersonalDays,
          pto_personal_accrual_rate: ptoPersonalAccrual,
          pto_hours_per_day: ptoHoursPerDay,
        }),
      });
      setPtoSuccess('PTO settings updated successfully');
      setTimeout(() => setPtoSuccess(''), 3000);
    } catch (err: any) { setPtoError(err.message || 'Failed to save PTO settings'); }
    finally { setPtoSaving(false); }
  };

  // Rewards load/save
  const loadRewardsSettings = async () => {
    setRewardLoading(true);
    try {
      const data = await apiRequest('/system-settings/rewards');
      setRewardStepPoints(data.step_completion_reward_points ?? 5);
      setRewardSilver(data.reward_tier_silver_threshold ?? 2000);
      setRewardGold(data.reward_tier_gold_threshold ?? 5000);
      setRewardPlatinum(data.reward_tier_platinum_threshold ?? 10000);
    } catch (err) { console.error('Failed to load rewards settings:', err); }
    finally { setRewardLoading(false); }
  };

  const saveRewardsSettings = async () => {
    setRewardSaving(true); setRewardError(''); setRewardSuccess('');
    try {
      await apiRequest('/system-settings/rewards', {
        method: 'POST',
        body: JSON.stringify({
          step_completion_reward_points: rewardStepPoints,
          reward_tier_silver_threshold: rewardSilver,
          reward_tier_gold_threshold: rewardGold,
          reward_tier_platinum_threshold: rewardPlatinum,
        }),
      });
      setRewardSuccess('Rewards settings updated successfully');
      setTimeout(() => setRewardSuccess(''), 3000);
    } catch (err: any) { setRewardError(err.message || 'Failed to save rewards settings'); }
    finally { setRewardSaving(false); }
  };

  // Discipline load/save
  const loadDisciplineSettings = async () => {
    setDiscLoading(true);
    try {
      const data = await apiRequest('/system-settings/discipline');
      setDiscMaxInfractions(data.discipline_lifetime_max_infractions ?? 100);
      setDiscPointsEnabled(data.discipline_points_deduction_enabled ?? true);
      setDiscDefaultPoints(data.discipline_default_points_deduction ?? 50);
    } catch (err) { console.error('Failed to load discipline settings:', err); }
    finally { setDiscLoading(false); }
  };

  const saveDisciplineSettings = async () => {
    setDiscSaving(true); setDiscError(''); setDiscSuccess('');
    try {
      await apiRequest('/system-settings/discipline', {
        method: 'POST',
        body: JSON.stringify({
          discipline_lifetime_max_infractions: discMaxInfractions,
          discipline_points_deduction_enabled: discPointsEnabled,
          discipline_default_points_deduction: discDefaultPoints,
        }),
      });
      setDiscSuccess('Discipline settings updated successfully');
      setTimeout(() => setDiscSuccess(''), 3000);
    } catch (err: any) { setDiscError(err.message || 'Failed to save discipline settings'); }
    finally { setDiscSaving(false); }
  };

  // Training load/save
  const loadTrainingSettings = async () => {
    setTrainingLoading(true);
    try {
      const data = await apiRequest('/system-settings/training');
      setTrainingPassScore(data.training_default_passing_score ?? 80);
    } catch (err) { console.error('Failed to load training settings:', err); }
    finally { setTrainingLoading(false); }
  };

  const saveTrainingSettings = async () => {
    setTrainingSaving(true); setTrainingError(''); setTrainingSuccess('');
    try {
      await apiRequest('/system-settings/training', {
        method: 'POST',
        body: JSON.stringify({ training_default_passing_score: trainingPassScore }),
      });
      setTrainingSuccess('Training settings updated successfully');
      setTimeout(() => setTrainingSuccess(''), 3000);
    } catch (err: any) { setTrainingError(err.message || 'Failed to save training settings'); }
    finally { setTrainingSaving(false); }
  };

  // Home Portal App Management
  const [homeApps, setHomeApps] = useState<HomeApp[]>([]);
  const [homeAppsLoading, setHomeAppsLoading] = useState(false);
  const [homeAppsError, setHomeAppsError] = useState('');
  const [homeAppsSuccess, setHomeAppsSuccess] = useState('');
  const [editingApp, setEditingApp] = useState<HomeApp | null>(null);
  const [showAppForm, setShowAppForm] = useState(false);
  const [appFormSaving, setAppFormSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const PERMISSION_OPTIONS = [
    { value: '', label: 'None (any authenticated user)' },
    { value: 'factory_admin_access', label: 'Factory Admin' },
    { value: 'workstation_access', label: 'Workstation' },
    { value: 'sales_portal_access', label: 'Sales Portal' },
    { value: 'shipping_portal_access', label: 'Shipping Portal' },
    { value: 'accounting_access', label: 'Accounting' },
    { value: 'maintenance_access', label: 'Maintenance' },
  ];

  const emptyApp: Omit<HomeApp, 'id'> = {
    name: '',
    description: '',
    url: '',
    icon_type: 'emoji',
    icon_value: '',
    category: 'Uncategorized',
    bg_color: '#2563eb',
    text_color: '#ffffff',
    sort_order: 99,
    visible: true,
    requires_auth: false,
    required_permission: null,
    open_in: 'new_tab',
    audience: 'internal',
  };

  const [appForm, setAppForm] = useState<Omit<HomeApp, 'id'>>(emptyApp);

  const loadHomeApps = useCallback(async () => {
    setHomeAppsLoading(true);
    setHomeAppsError('');
    try {
      const data = await apiRequest('/home-portal/apps/all');
      setHomeApps(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setHomeAppsError(err.message || 'Failed to load apps');
    } finally {
      setHomeAppsLoading(false);
    }
  }, []);

  const openAddApp = (audience: string = 'internal') => {
    setEditingApp(null);
    setAppForm({ ...emptyApp, audience });
    setShowAppForm(true);
  };

  const openEditApp = (app: HomeApp) => {
    setEditingApp(app);
    setAppForm({
      name: app.name,
      description: app.description || '',
      url: app.url,
      icon_type: app.icon_type,
      icon_value: app.icon_value,
      category: app.category,
      bg_color: app.bg_color,
      text_color: app.text_color,
      sort_order: app.sort_order,
      visible: app.visible,
      requires_auth: app.requires_auth,
      required_permission: app.required_permission,
      open_in: app.open_in,
      audience: app.audience || 'internal',
    });
    setShowAppForm(true);
  };

  const saveApp = async () => {
    if (!appForm.name.trim() || !appForm.url.trim() || !appForm.icon_value.trim()) {
      setHomeAppsError('Name, URL, and Icon are required');
      return;
    }
    setAppFormSaving(true);
    setHomeAppsError('');
    try {
      const payload = {
        ...appForm,
        description: appForm.description || null,
        required_permission: appForm.required_permission || null,
      };
      if (editingApp) {
        await apiRequest(`/home-portal/apps/${editingApp.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
        setHomeAppsSuccess('App updated successfully');
      } else {
        await apiRequest('/home-portal/apps', { method: 'POST', body: JSON.stringify(payload) });
        setHomeAppsSuccess('App created successfully');
      }
      setShowAppForm(false);
      setTimeout(() => setHomeAppsSuccess(''), 3000);
      loadHomeApps();
    } catch (err: any) {
      setHomeAppsError(err.message || 'Failed to save app');
    } finally {
      setAppFormSaving(false);
    }
  };

  const deleteApp = async (id: string) => {
    try {
      await apiRequest(`/home-portal/apps/${id}`, { method: 'DELETE' });
      setHomeAppsSuccess('App deleted');
      setTimeout(() => setHomeAppsSuccess(''), 3000);
      setDeleteConfirm(null);
      loadHomeApps();
    } catch (err: any) {
      setHomeAppsError(err.message || 'Failed to delete app');
    }
  };

  useEffect(() => {
    loadAchSettings();
    loadPricingSettings();
    loadRepFirms();
    loadLeadTimes();
    loadShippingMarkup();
    loadPowderCoatCharge();
    loadComponentColorCharge();
    loadPtoSettings();
    loadRewardsSettings();
    loadDisciplineSettings();
    loadTrainingSettings();
    setLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'homeportal' || activeTab === 'dealerhome') loadHomeApps();
  }, [activeTab, loadHomeApps]);

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Configure factory settings"
      />

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {([
          ['pricing', 'Pricing'],
          ['leadtimes', 'Lead Times'],
          ['pto', 'PTO & Time Off'],
          ['rewards', 'Employee Rewards'],
          ['discipline', 'Discipline'],
          ['training', 'Training'],
          ['homeportal', 'Home Portal'],
          ['dealerhome', 'Dealer Portal'],
          ['banner', 'System Banner'],
          ['promotions', 'Promotions'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={clsx(
              'px-4 py-2 rounded-lg font-medium transition-colors text-sm',
              activeTab === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'pricing' && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Pricing Settings</h2>

          {/* Price Increase */}
          <div className="border rounded-lg p-6 bg-gray-50">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">Price Increase Percentage</h3>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  Increase all product prices by this percentage from the 2025 base prices.
                  This is applied before dealer discounts are calculated.
                </p>

                {/* Warning Box */}
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> This % is cumulative from 2025 base prices. If currently set to 10% and you want to add another 10%, enter <strong>20%</strong> (not 10%).
                    </p>
                  </div>
                </div>

                {priceError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {priceError}
                  </div>
                )}

                {priceSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    {priceSuccess}
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 min-w-[220px]">
                      <label htmlFor="priceIncrease" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        Price Increase:
                      </label>
                      <input
                        type="number"
                        id="priceIncrease"
                        value={priceIncreasePercent}
                        onChange={(e) => setPriceIncreasePercent(Number(e.target.value))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min={0}
                        max={100}
                        step={1}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                        disabled={priceLoading}
                      />
                      <span className="text-gray-500">%</span>
                    </div>
                    <span className="text-xs text-gray-400">Applied to screen unit MSRP</span>
                  </div>
                  <div className="ml-0">
                    <input
                      type="text"
                      value={priceIncreaseReason}
                      onChange={(e) => setPriceIncreaseReason(e.target.value)}
                      placeholder="Reason for change (e.g., 2026 cost adjustment)"
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={priceLoading}
                    />
                    {priceIncreaseMeta.changedAt && (
                      <p className="text-xs text-gray-400 mt-1">
                        Last changed: {new Date(priceIncreaseMeta.changedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        {priceIncreaseMeta.reason && <> — <em>"{priceIncreaseMeta.reason}"</em></>}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 min-w-[220px]">
                      <label htmlFor="tariffPercent" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        Tariff Surcharge:
                      </label>
                      <input
                        type="number"
                        id="tariffPercent"
                        value={tariffPercent}
                        onChange={(e) => setTariffPercent(Number(e.target.value))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min={0}
                        max={100}
                        step={0.5}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                        disabled={priceLoading}
                      />
                      <span className="text-gray-500">%</span>
                    </div>
                    <span className="text-xs text-gray-400">Import tariff pass-through</span>
                  </div>
                  <div className="ml-0">
                    <input
                      type="text"
                      value={tariffReason}
                      onChange={(e) => setTariffReason(e.target.value)}
                      placeholder="Reason for change (e.g., China tariff increase to 145%)"
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={priceLoading}
                    />
                    {tariffMeta.changedAt && (
                      <p className="text-xs text-gray-400 mt-1">
                        Last changed: {new Date(tariffMeta.changedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        {tariffMeta.reason && <> — <em>"{tariffMeta.reason}"</em></>}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-4 pt-2 border-t border-gray-200">
                    <div className="flex items-center gap-2 min-w-[220px]">
                      <label htmlFor="openRollDiscount" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        Open Roll Discount:
                      </label>
                      <input
                        type="number"
                        id="openRollDiscount"
                        value={openRollDiscount}
                        onChange={(e) => setOpenRollDiscount(Number(e.target.value))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min={0}
                        max={20}
                        step={0.5}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                        disabled={priceLoading}
                      />
                      <span className="text-gray-500">%</span>
                    </div>
                    <span className="text-xs text-gray-400">% off dealer cost for Open Roll units (no housing box)</span>
                  </div>

                  {(priceIncreasePercent > 0 || tariffPercent > 0) && (
                    <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-800">
                        <strong>Effective markup:</strong> {(((1 + priceIncreasePercent / 100) * (1 + tariffPercent / 100) - 1) * 100).toFixed(1)}% —
                        a $1,000 base screen becomes <strong>${((1 + priceIncreasePercent / 100) * (1 + tariffPercent / 100) * 1000).toFixed(0)}</strong>
                      </p>
                    </div>
                  )}

                  <button
                    onClick={savePriceIncrease}
                    disabled={priceSaving || priceLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {priceSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>

                <p className="text-xs text-gray-400 mt-3">
                  Both percentages stack multiplicatively on screen unit MSRP only. Accessories, u-channel, and custom color upcharges are not affected.
                </p>
              </div>
            </div>
          </div>

          {/* Rep Firm Pricing */}
          <div className="border rounded-lg p-6 bg-gray-50 mt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Rep Firm MSRP Adjustment</h3>
                    <p className="text-sm text-gray-500 mt-1 mb-4">
                      Adjust the MSRP for each rep firm's dealers. Positive % increases price, negative % decreases.
                      House and direct dealers are not affected. This stacks with the global Price Increase and Tariff above.
                    </p>
                  </div>
                  <button
                    onClick={openAddRepFirm}
                    className="flex-shrink-0 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg shadow-sm whitespace-nowrap"
                  >
                    + Add Rep Firm
                  </button>
                </div>
                {repFirms.length === 0 && (
                  <p className="text-sm text-gray-400 italic mb-3">No rep firms yet. Click "+ Add Rep Firm" to create one.</p>
                )}

                  <div className="space-y-3">
                    {repFirms.map((firm: any) => {
                      const adj = parseFloat(firm.price_adjustment_percent) || 0;
                      const globalMultiplier = (1 + priceIncreasePercent / 100) * (1 + tariffPercent / 100);
                      const firmMultiplier = globalMultiplier * (1 + adj / 100);
                      return (
                        <div key={firm.id} className="flex items-center gap-4 p-3 bg-white border rounded-lg">
                          <div className="flex-1">
                            <span className="font-medium text-gray-900">{firm.name}</span>
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${firm.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {firm.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Adjustment:</label>
                            <input
                              type="number"
                              step="0.1"
                              defaultValue={adj}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                if (val !== adj) saveRepFirmPricing(firm.id, val);
                              }}
                              className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-center text-sm focus:ring-2 focus:ring-purple-500"
                              disabled={repFirmSaving === firm.id}
                            />
                            <span className="text-gray-500 text-sm">%</span>
                          </div>
                          <div className="text-right min-w-[180px]">
                            <p className="text-xs text-gray-500">
                              Effective: <strong className="text-gray-700">{((firmMultiplier - 1) * 100).toFixed(1)}%</strong> total markup
                            </p>
                            <p className="text-xs text-gray-400">
                              $1,000 base → <strong>${(firmMultiplier * 1000).toFixed(0)}</strong> MSRP
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                <p className="text-xs text-gray-400 mt-3">
                  Rep firm dealers get a 50% discount off this adjusted MSRP. The margin between house MSRP and rep firm MSRP is your rep firm overhead.
                </p>
              </div>
            </div>
          </div>

          {/* Custom Powder Coating Charge */}
          <div className="border rounded-lg p-6 bg-gray-50 mt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">Custom Powder Coating Charge</h3>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  Additional charge applied when customer selects a custom RAL color for powder coating.
                  This covers the cost of special color matching and custom coating.
                </p>

                {powderCoatError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {powderCoatError}
                  </div>
                )}

                {powderCoatSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    {powderCoatSuccess}
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label htmlFor="powderCoatCharge" className="text-sm font-medium text-gray-700">
                      Charge Amount:
                    </label>
                    <span className="text-gray-500">$</span>
                    <input
                      type="number"
                      id="powderCoatCharge"
                      value={powderCoatCharge}
                      onChange={(e) => setPowderCoatCharge(Number(e.target.value))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min={0}
                      step={1}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                      disabled={powderCoatLoading}
                    />
                  </div>

                  <button
                    onClick={savePowderCoatCharge}
                    disabled={powderCoatSaving || powderCoatLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {powderCoatSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>

                <p className="text-xs text-gray-400 mt-3">
                  This charge is added per screen when customer chooses "Custom" frame color with a RAL number.
                </p>
              </div>
            </div>
          </div>

          {/* Custom Component Color Charge */}
          <div className="border rounded-lg p-6 bg-gray-50 mt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">Custom Component Color Charge</h3>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  Additional charge per component when a dealer overrides individual component colors
                  (housing, weight bar, left track, right track). Each component with a custom color different
                  from the frame color incurs this charge.
                </p>

                {componentColorError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {componentColorError}
                  </div>
                )}

                {componentColorSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    {componentColorSuccess}
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label htmlFor="componentColorCharge" className="text-sm font-medium text-gray-700">
                      Charge Per Component:
                    </label>
                    <span className="text-gray-500">$</span>
                    <input
                      type="number"
                      id="componentColorCharge"
                      value={componentColorCharge}
                      onChange={(e) => setComponentColorCharge(Number(e.target.value))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min={0}
                      step={1}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                      disabled={componentColorLoading}
                    />
                  </div>

                  <button
                    onClick={saveComponentColorCharge}
                    disabled={componentColorSaving || componentColorLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {componentColorSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>

                <p className="text-xs text-gray-400 mt-3">
                  Example: If a dealer sets housing and left track to custom colors ($245 each), the additional charge is $490 for that screen.
                </p>
              </div>
            </div>
          </div>

          {/* ACH Bank Payment Discount */}
          <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-6">Payment Settings</h2>
          <div className="border rounded-lg p-6 bg-gray-50">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">ACH / Bank Payment Discounts</h3>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  Offer customers a discount for paying via bank transfer instead of credit card.
                  Set separate discounts for each payment processor.
                </p>

                {achError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {achError}
                  </div>
                )}

                {achSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    {achSuccess}
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 min-w-[280px]">
                      <label htmlFor="achDiscount" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        Stripe ACH Discount:
                      </label>
                      <input
                        type="number"
                        id="achDiscount"
                        value={achDiscountPercent}
                        onChange={(e) => setAchDiscountPercent(Number(e.target.value))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min={0}
                        max={10}
                        step={0.5}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                        disabled={achLoading}
                      />
                      <span className="text-gray-500">%</span>
                    </div>
                    <span className="text-xs text-gray-400">Applied to Stripe ACH bank payments</span>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 min-w-[280px]">
                      <label htmlFor="authorizeAchDiscount" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        Authorize.net ACH Discount:
                      </label>
                      <input
                        type="number"
                        id="authorizeAchDiscount"
                        value={authorizeAchDiscountPercent}
                        onChange={(e) => setAuthorizeAchDiscountPercent(Number(e.target.value))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min={0}
                        max={10}
                        step={0.5}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                        disabled={achLoading}
                      />
                      <span className="text-gray-500">%</span>
                    </div>
                    <span className="text-xs text-gray-400">Applied to Authorize.net bank payments</span>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 min-w-[280px]">
                      <label htmlFor="helcimAchDiscount" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        Helcim ACH Discount:
                      </label>
                      <input
                        type="number"
                        id="helcimAchDiscount"
                        value={helcimAchDiscountPercent}
                        onChange={(e) => setHelcimAchDiscountPercent(Number(e.target.value))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min={0}
                        max={10}
                        step={0.5}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                        disabled={achLoading}
                      />
                      <span className="text-gray-500">%</span>
                    </div>
                    <span className="text-xs text-gray-400">Applied to Helcim ACH bank payments</span>
                  </div>

                  <button
                    onClick={saveAchSettings}
                    disabled={achSaving || achLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {achSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>

                <p className="text-xs text-gray-400 mt-3">
                  Example: A 2% discount on a $5,000 order saves the customer $100 when paying via bank transfer.
                </p>
              </div>
            </div>
          </div>

          {/* CC Processing-Fee Surcharge */}
          <div className="border rounded-lg p-6 bg-gray-50 mt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">Credit Card Processing-Fee Surcharge</h3>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  Pass Stripe processing fees through to dealers paying by credit card. Dealer portal flows
                  using Stripe Elements (multi-invoice pay) detect the funding type before charge and skip
                  the fee on debit/prepaid cards. Stripe Checkout flows charge optimistically and
                  auto-refund the surcharge if a debit card is detected post-charge. Always skipped for
                  dealers billed in: <span className="font-mono">{ccSurchargeStates.join(', ')}</span>.
                </p>

                {ccSurchargeError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{ccSurchargeError}</div>
                )}
                {ccSurchargeSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{ccSurchargeSuccess}</div>
                )}

                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ccSurchargeEnabled}
                      onChange={(e) => setCcSurchargeEnabled(e.target.checked)}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Enable CC surcharge {ccSurchargeEnabled ? '(ON — fees being collected)' : '(OFF — no fees added)'}
                    </span>
                  </label>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 min-w-[280px]">
                      <label htmlFor="ccSurchargePercent" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        Surcharge Rate:
                      </label>
                      <input
                        type="number"
                        id="ccSurchargePercent"
                        value={ccSurchargePercent}
                        onChange={(e) => setCcSurchargePercent(Number(e.target.value))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min={0}
                        max={5}
                        step={0.1}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-center"
                        disabled={!ccSurchargeEnabled || ccSurchargeSaving}
                      />
                      <span className="text-gray-500">%</span>
                    </div>
                    <span className="text-xs text-gray-400">
                      Card-network ceiling is 3%. Recommended: 3% (matches Stripe's ~2.9% + $0.30 effective rate)
                    </span>
                  </div>

                  <button
                    onClick={saveCcSurchargeSettings}
                    disabled={ccSurchargeSaving}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {ccSurchargeSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>

                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                  <strong>Before turning ON:</strong> Visa/Mastercard technically require 30 days' written
                  notice before launching a surcharge program. Send dealers a heads-up email first.
                </div>
              </div>
            </div>
          </div>

          {/* Weight Bar Kit */}
          <div className="border rounded-lg p-6 bg-gray-50 mt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">Weight Bar Kit (auto-added on small screens)</h3>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  Auto-added as an accessory line on the invoice whenever any screen's width is below the
                  threshold. Two powder-coated steel bars (2 ft shy of weight bar width) + bolts + labor.
                  Dealer can opt out per-screen via "Skip kit" checkbox on the order form.
                </p>

                {wbkError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{wbkError}</div>
                )}
                {wbkSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{wbkSuccess}</div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                  <div>
                    <label htmlFor="wbkPrice" className="block text-sm font-medium text-gray-700 mb-1">
                      Price per kit
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">$</span>
                      <input
                        type="number"
                        id="wbkPrice"
                        value={wbkPrice}
                        onChange={(e) => setWbkPrice(Number(e.target.value))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min={0}
                        step={1}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-center"
                        disabled={wbkSaving}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="wbkThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                      Trigger threshold (screen width)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        id="wbkThreshold"
                        value={wbkThreshold}
                        onChange={(e) => setWbkThreshold(Number(e.target.value))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min={12}
                        max={200}
                        step={1}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-center"
                        disabled={wbkSaving}
                      />
                      <span className="text-gray-500">"</span>
                      <span className="text-xs text-gray-400">(under this width)</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={saveWeightBarKit}
                  disabled={wbkSaving}
                  className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  {wbkSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          {/* Shipping Markup Settings */}
          <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-6">Shipping Settings</h2>

          <div className="border rounded-lg p-6 bg-gray-50">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">Freight Quote Markup</h3>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  Add a markup percentage to carrier freight quotes before showing them to dealers.
                  This helps cover handling costs and provides margin on shipping.
                </p>

                {shippingError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {shippingError}
                  </div>
                )}

                {shippingSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    {shippingSuccess}
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label htmlFor="shippingMarkup" className="text-sm font-medium text-gray-700">
                      Markup Percentage:
                    </label>
                    <input
                      type="number"
                      id="shippingMarkup"
                      value={shippingMarkup}
                      onChange={(e) => setShippingMarkup(Number(e.target.value))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min={0}
                      max={100}
                      step={0.5}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                      disabled={shippingLoading}
                    />
                    <span className="text-gray-500">%</span>
                  </div>

                  <button
                    onClick={saveShippingMarkup}
                    disabled={shippingSaving || shippingLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {shippingSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>

                <p className="text-xs text-gray-400 mt-3">
                  Example: A 15% markup on a $500 carrier quote results in a $575 freight charge to the dealer.
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'pricing' && <SmsNotificationToggle />}

      {activeTab === 'leadtimes' && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Production Lead Times</h2>

          <div className="border rounded-lg p-6 bg-gray-50">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">Lead Times by Fabric Type</h3>
                <p className="text-sm text-gray-500 mt-1 mb-6">
                  Configure production lead times in business days for each fabric type.
                  These times are used to calculate estimated completion dates.
                </p>

                {leadTimeError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {leadTimeError}
                  </div>
                )}

                {leadTimeSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    {leadTimeSuccess}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-32">Solar Fabrics:</label>
                    <input
                      type="number"
                      value={leadTimes.solar}
                      onChange={(e) => setLeadTimes(prev => ({ ...prev, solar: Number(e.target.value) }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min={1}
                      max={60}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                      disabled={leadTimeLoading}
                    />
                    <span className="text-gray-500 text-sm">days</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-32">Premium Solar:</label>
                    <input
                      type="number"
                      value={leadTimes.premiumSolar}
                      onChange={(e) => setLeadTimes(prev => ({ ...prev, premiumSolar: Number(e.target.value) }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min={1}
                      max={60}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                      disabled={leadTimeLoading}
                    />
                    <span className="text-gray-500 text-sm">days</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-32">Vinyl (CVW):</label>
                    <input
                      type="number"
                      value={leadTimes.cvw}
                      onChange={(e) => setLeadTimes(prev => ({ ...prev, cvw: Number(e.target.value) }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min={1}
                      max={60}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                      disabled={leadTimeLoading}
                    />
                    <span className="text-gray-500 text-sm">days</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-32">Insect Screen:</label>
                    <input
                      type="number"
                      value={leadTimes.insect}
                      onChange={(e) => setLeadTimes(prev => ({ ...prev, insect: Number(e.target.value) }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min={1}
                      max={60}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                      disabled={leadTimeLoading}
                    />
                    <span className="text-gray-500 text-sm">days</span>
                  </div>
                </div>

                <button
                  onClick={saveLeadTimes}
                  disabled={leadTimeSaving || leadTimeLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {leadTimeSaving ? 'Saving...' : 'Save Lead Times'}
                </button>

                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Lead times are in business days. Orders placed before 12pm CT start the next business day.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
      {(activeTab === 'homeportal' || activeTab === 'dealerhome') && (
        <Card>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {activeTab === 'dealerhome' ? 'Dealer Portal Apps' : 'Home Portal Apps'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {activeTab === 'dealerhome' ? 'Manage tiles shown on dealer.kederscreens.com' : 'Manage tiles shown on home.kederscreens.com'}
              </p>
            </div>
            <button
              onClick={() => openAddApp(activeTab === 'dealerhome' ? 'dealer' : 'internal')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              + Add App
            </button>
          </div>

          {homeAppsError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {homeAppsError}
            </div>
          )}

          {homeAppsSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              {homeAppsSuccess}
            </div>
          )}

          {(() => {
            const audienceFilter = activeTab === 'dealerhome' ? 'dealer' : 'internal';
            const filteredApps = homeApps.filter(a => a.audience === audienceFilter || a.audience === 'both');

            if (homeAppsLoading) return <div className="text-center py-12"><LoadingSpinner /></div>;
            if (filteredApps.length === 0) return <div className="text-center py-12 text-gray-500">No apps configured yet</div>;

            const grouped: Record<string, HomeApp[]> = {};
            for (const app of filteredApps) {
              const cat = app.category || 'Uncategorized';
              if (!grouped[cat]) grouped[cat] = [];
              grouped[cat].push(app);
            }
            const sortedCategories = Object.entries(grouped).sort(
              ([, a], [, b]) => (a[0]?.sort_order ?? 99) - (b[0]?.sort_order ?? 99)
            );
            return (
              <div className="overflow-x-auto">
                {sortedCategories.map(([category, apps]) => (
                  <div key={category} className="mb-6">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2 px-3">{category}</h4>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-2 pl-3 font-medium text-gray-500 text-xs">#</th>
                          <th className="pb-2 font-medium text-gray-500 text-xs">Icon</th>
                          <th className="pb-2 font-medium text-gray-500 text-xs">Name</th>
                          <th className="pb-2 font-medium text-gray-500 text-xs">URL</th>
                          <th className="pb-2 font-medium text-gray-500 text-xs">Visible</th>
                          <th className="pb-2 pr-3 font-medium text-gray-500 text-xs text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apps.map((app) => (
                          <tr key={app.id} className="border-b hover:bg-gray-50">
                            <td className="py-2 pl-3 text-gray-400 text-xs">{app.sort_order}</td>
                            <td className="py-2">
                              <span className="text-lg" style={{ backgroundColor: app.bg_color, color: app.text_color, padding: '2px 5px', borderRadius: '5px' }}>
                                {app.icon_value}
                              </span>
                            </td>
                            <td className="py-2 font-medium text-gray-900">
                              {app.name}
                              {app.audience === 'both' && (
                                <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">Both</span>
                              )}
                            </td>
                            <td className="py-2 text-gray-500 max-w-[220px] truncate text-xs">{app.url}</td>
                            <td className="py-2">
                              {app.visible ? (
                                <span className="text-green-600 text-xs">Yes</span>
                              ) : (
                                <span className="text-red-500 text-xs">Hidden</span>
                              )}
                            </td>
                            <td className="py-2 pr-3 text-right">
                              <button onClick={() => openEditApp(app)} className="text-blue-600 hover:text-blue-800 mr-3 text-sm">Edit</button>
                              {deleteConfirm === app.id ? (
                                <>
                                  <button onClick={() => deleteApp(app.id)} className="text-red-600 hover:text-red-800 mr-1 text-sm font-medium">Confirm</button>
                                  <button onClick={() => setDeleteConfirm(null)} className="text-gray-500 hover:text-gray-700 text-sm">Cancel</button>
                                </>
                              ) : (
                                <button onClick={() => setDeleteConfirm(app.id)} className="text-red-500 hover:text-red-700 text-sm">Delete</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Add/Edit Modal */}
          {showAppForm && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAppForm(false)}>
              <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {editingApp ? 'Edit App' : 'Add App'}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={appForm.name}
                      onChange={(e) => setAppForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Dealer Portal"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={appForm.description || ''}
                      onChange={(e) => setAppForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Short description of the app"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                    <input
                      type="text"
                      value={appForm.url}
                      onChange={(e) => setAppForm(prev => ({ ...prev, url: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Icon (emoji) *</label>
                      <input
                        type="text"
                        value={appForm.icon_value}
                        onChange={(e) => setAppForm(prev => ({ ...prev, icon_value: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-2xl"
                        placeholder="🏠"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select
                        value={[...new Set(homeApps.map(a => a.category))].includes(appForm.category) ? appForm.category : '__custom__'}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') {
                            setAppForm(prev => ({ ...prev, category: '' }));
                          } else {
                            setAppForm(prev => ({ ...prev, category: e.target.value }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        {[...new Set(homeApps.map(a => a.category))].sort().map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                        <option value="__custom__">+ New Category...</option>
                      </select>
                      {![...new Set(homeApps.map(a => a.category))].includes(appForm.category) && (
                        <input
                          type="text"
                          value={appForm.category}
                          onChange={(e) => setAppForm(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="New category name"
                          autoFocus
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="app_both_portals"
                      checked={appForm.audience === 'both'}
                      onChange={(e) => setAppForm(prev => ({
                        ...prev,
                        audience: e.target.checked ? 'both' : (activeTab === 'dealerhome' ? 'dealer' : 'internal'),
                      }))}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <label htmlFor="app_both_portals" className="text-sm text-gray-700">
                      Show on both Home Portal &amp; Dealer Portal
                    </label>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bg Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={appForm.bg_color}
                          onChange={(e) => setAppForm(prev => ({ ...prev, bg_color: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer border"
                        />
                        <input
                          type="text"
                          value={appForm.bg_color}
                          onChange={(e) => setAppForm(prev => ({ ...prev, bg_color: e.target.value }))}
                          className="flex-1 px-2 py-2 border border-gray-300 rounded-lg text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={appForm.text_color}
                          onChange={(e) => setAppForm(prev => ({ ...prev, text_color: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer border"
                        />
                        <input
                          type="text"
                          value={appForm.text_color}
                          onChange={(e) => setAppForm(prev => ({ ...prev, text_color: e.target.value }))}
                          className="flex-1 px-2 py-2 border border-gray-300 rounded-lg text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                      <input
                        type="number"
                        value={appForm.sort_order}
                        onChange={(e) => setAppForm(prev => ({ ...prev, sort_order: Number(e.target.value) }))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min={0}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="app_visible"
                        checked={appForm.visible}
                        onChange={(e) => setAppForm(prev => ({ ...prev, visible: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <label htmlFor="app_visible" className="text-sm text-gray-700">Visible</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="app_requires_auth"
                        checked={appForm.requires_auth}
                        onChange={(e) => setAppForm(prev => ({ ...prev, requires_auth: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <label htmlFor="app_requires_auth" className="text-sm text-gray-700">Requires Login</label>
                    </div>
                  </div>

                  {appForm.requires_auth && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Required Permission</label>
                      <select
                        value={appForm.required_permission || ''}
                        onChange={(e) => setAppForm(prev => ({ ...prev, required_permission: e.target.value || null }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        {PERMISSION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Open In</label>
                    <select
                      value={appForm.open_in}
                      onChange={(e) => setAppForm(prev => ({ ...prev, open_in: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="new_tab">New Tab</option>
                      <option value="same_window">Same Window</option>
                    </select>
                  </div>

                  {/* Preview */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Preview</p>
                    <div className="rounded-xl overflow-hidden inline-block border" style={{ minWidth: '200px' }}>
                      <div className="px-5 pt-4 pb-3 flex items-center gap-3" style={{ backgroundColor: appForm.bg_color }}>
                        <span className="text-2xl" style={{ color: appForm.text_color }}>{appForm.icon_value || '?'}</span>
                        <span className="font-semibold text-sm" style={{ color: appForm.text_color }}>{appForm.name || 'App Name'}</span>
                      </div>
                      <div className="px-5 py-2 bg-white">
                        <p className="text-xs text-gray-500">{appForm.description || 'No description'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6 pt-4 border-t">
                  <button
                    onClick={() => setShowAppForm(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveApp}
                    disabled={appFormSaving}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {appFormSaving ? 'Saving...' : editingApp ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'pto' && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">PTO & Time Off Settings</h2>
          <p className="text-sm text-gray-500 mb-6">Default PTO allocations for new employees. Changes apply to newly initialized balances.</p>

          <div className="border rounded-lg p-6 bg-gray-50">
            {ptoError && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{ptoError}</div>}
            {ptoSuccess && <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{ptoSuccess}</div>}

            <div className="space-y-6">
              {/* Vacation */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Vacation</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-36">Annual Days:</label>
                    <input type="number" value={ptoVacationDays} onChange={(e) => setPtoVacationDays(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} max={60} step={0.5} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={ptoLoading} />
                    <span className="text-gray-500 text-sm">days/year</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-36">Monthly Accrual:</label>
                    <input type="number" value={ptoVacationAccrual} onChange={(e) => setPtoVacationAccrual(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} max={5} step={0.125} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={ptoLoading} />
                    <span className="text-gray-500 text-sm">days/month</span>
                  </div>
                </div>
              </div>

              {/* Sick */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Sick Leave</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-36">Annual Days:</label>
                    <input type="number" value={ptoSickDays} onChange={(e) => setPtoSickDays(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} max={60} step={0.5} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={ptoLoading} />
                    <span className="text-gray-500 text-sm">days/year</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-36">Monthly Accrual:</label>
                    <input type="number" value={ptoSickAccrual} onChange={(e) => setPtoSickAccrual(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} max={5} step={0.125} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={ptoLoading} />
                    <span className="text-gray-500 text-sm">days/month</span>
                  </div>
                </div>
              </div>

              {/* Personal */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Personal</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-36">Annual Days:</label>
                    <input type="number" value={ptoPersonalDays} onChange={(e) => setPtoPersonalDays(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} max={60} step={0.5} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={ptoLoading} />
                    <span className="text-gray-500 text-sm">days/year</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-36">Monthly Accrual:</label>
                    <input type="number" value={ptoPersonalAccrual} onChange={(e) => setPtoPersonalAccrual(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} max={5} step={0.125} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={ptoLoading} />
                    <span className="text-gray-500 text-sm">days/month</span>
                  </div>
                </div>
              </div>

              {/* Hours Per Day */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">General</h3>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700 w-36">Hours Per Day:</label>
                  <input type="number" value={ptoHoursPerDay} onChange={(e) => setPtoHoursPerDay(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={1} max={24} step={0.5} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={ptoLoading} />
                  <span className="text-gray-500 text-sm">hours</span>
                </div>
                <p className="text-xs text-gray-400 mt-2 ml-[9.5rem]">Used to convert between hours requested and PTO days consumed.</p>
              </div>
            </div>

            <button onClick={savePtoSettings} disabled={ptoSaving || ptoLoading} className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {ptoSaving ? 'Saving...' : 'Save PTO Settings'}
            </button>
          </div>
        </Card>
      )}

      {activeTab === 'rewards' && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Employee Rewards Settings</h2>
          <p className="text-sm text-gray-500 mb-6">Configure reward points and tier thresholds for the employee rewards program.</p>

          <div className="border rounded-lg p-6 bg-gray-50">
            {rewardError && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{rewardError}</div>}
            {rewardSuccess && <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{rewardSuccess}</div>}

            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Points Earning</h3>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700 w-48">Points Per Step Completion:</label>
                  <input type="number" value={rewardStepPoints} onChange={(e) => setRewardStepPoints(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} max={1000} step={1} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={rewardLoading} />
                  <span className="text-gray-500 text-sm">points</span>
                </div>
                <p className="text-xs text-gray-400 mt-2 ml-[12.5rem]">Points awarded each time an employee completes a production step.</p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Tier Thresholds</h3>
                <p className="text-xs text-gray-500 mb-3">Total lifetime points needed to reach each tier.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-500 w-20">Silver:</label>
                    <input type="number" value={rewardSilver} onChange={(e) => setRewardSilver(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} step={100} className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={rewardLoading} />
                    <span className="text-gray-500 text-sm">pts</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-yellow-600 w-20">Gold:</label>
                    <input type="number" value={rewardGold} onChange={(e) => setRewardGold(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} step={100} className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={rewardLoading} />
                    <span className="text-gray-500 text-sm">pts</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-purple-600 w-20">Platinum:</label>
                    <input type="number" value={rewardPlatinum} onChange={(e) => setRewardPlatinum(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} step={100} className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={rewardLoading} />
                    <span className="text-gray-500 text-sm">pts</span>
                  </div>
                </div>
              </div>
            </div>

            <button onClick={saveRewardsSettings} disabled={rewardSaving || rewardLoading} className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {rewardSaving ? 'Saving...' : 'Save Rewards Settings'}
            </button>
          </div>
        </Card>
      )}

      {activeTab === 'discipline' && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Discipline Settings</h2>
          <p className="text-sm text-gray-500 mb-6">Configure discipline escalation and reward point deductions for infractions.</p>

          <div className="border rounded-lg p-6 bg-gray-50">
            {discError && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{discError}</div>}
            {discSuccess && <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{discSuccess}</div>}

            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Escalation</h3>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700 w-52">Lifetime Max Infractions:</label>
                  <input type="number" value={discMaxInfractions} onChange={(e) => setDiscMaxInfractions(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={1} max={999} step={1} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={discLoading} />
                </div>
                <p className="text-xs text-gray-400 mt-2 ml-[13.5rem]">When an employee reaches this many total infractions, the system raises a draft for review flagged as the lifetime maximum. It never issues a warning or ends employment on its own — a person decides what happens.</p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Reward Points Deduction</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="discPointsEnabled" checked={discPointsEnabled} onChange={(e) => setDiscPointsEnabled(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" disabled={discLoading} />
                    <label htmlFor="discPointsEnabled" className="text-sm font-medium text-gray-700">Deduct reward points on infraction</label>
                  </div>
                  {discPointsEnabled && (
                    <div className="flex items-center gap-3 ml-7">
                      <label className="text-sm font-medium text-gray-700 w-44">Default Deduction:</label>
                      <input type="number" value={discDefaultPoints} onChange={(e) => setDiscDefaultPoints(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} max={10000} step={5} className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={discLoading} />
                      <span className="text-gray-500 text-sm">points</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button onClick={saveDisciplineSettings} disabled={discSaving || discLoading} className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {discSaving ? 'Saving...' : 'Save Discipline Settings'}
            </button>
          </div>
        </Card>
      )}

      {activeTab === 'training' && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Training Settings</h2>
          <p className="text-sm text-gray-500 mb-6">Default settings for new training modules.</p>

          <div className="border rounded-lg p-6 bg-gray-50">
            {trainingError && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{trainingError}</div>}
            {trainingSuccess && <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{trainingSuccess}</div>}

            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 w-44">Default Passing Score:</label>
              <input type="number" value={trainingPassScore} onChange={(e) => setTrainingPassScore(Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} min={0} max={100} step={5} className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center" disabled={trainingLoading} />
              <span className="text-gray-500 text-sm">%</span>
            </div>
            <p className="text-xs text-gray-400 mt-2 ml-[11.5rem]">Score needed to pass a training module when no custom score is set.</p>

            <button onClick={saveTrainingSettings} disabled={trainingSaving || trainingLoading} className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {trainingSaving ? 'Saving...' : 'Save Training Settings'}
            </button>
          </div>
        </Card>
      )}

      {activeTab === 'banner' && (
        <SystemBannerTab />
      )}

      {activeTab === 'promotions' && (
        <PromotionsTab />
      )}

      {/* Add Rep Firm Modal */}
      {showAddRepFirm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Add Rep Firm</h2>
              <button
                onClick={() => setShowAddRepFirm(false)}
                disabled={addRepFirmSaving}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="px-6 py-4 space-y-5">
              {addRepFirmError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{addRepFirmError}</div>
              )}
              {addRepFirmSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm whitespace-pre-wrap">{addRepFirmSuccess}</div>
              )}

              {/* Firm info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2 uppercase tracking-wide">Firm</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Firm Name *</label>
                    <input
                      type="text"
                      value={newRepFirm.name}
                      onChange={e => setNewRepFirm({ ...newRepFirm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Innovative Consulting and Marketing"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Primary Contact Name</label>
                    <input
                      type="text"
                      value={newRepFirm.contact_name}
                      onChange={e => setNewRepFirm({ ...newRepFirm, contact_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Patrick Boyer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Firm Phone</label>
                    <input
                      type="tel"
                      value={newRepFirm.contact_phone}
                      onChange={e => setNewRepFirm({ ...newRepFirm, contact_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="480-205-6903"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Firm Email</label>
                    <input
                      type="email"
                      value={newRepFirm.contact_email}
                      onChange={e => setNewRepFirm({ ...newRepFirm, contact_email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="info@example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2 uppercase tracking-wide">Address</h3>
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                  <div className="sm:col-span-6">
                    <input
                      type="text"
                      value={newRepFirm.street}
                      onChange={e => setNewRepFirm({ ...newRepFirm, street: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Street"
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <input
                      type="text"
                      value={newRepFirm.city}
                      onChange={e => setNewRepFirm({ ...newRepFirm, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={newRepFirm.state}
                      onChange={e => setNewRepFirm({ ...newRepFirm, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="ST"
                      maxLength={2}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <input
                      type="text"
                      value={newRepFirm.zip}
                      onChange={e => setNewRepFirm({ ...newRepFirm, zip: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="ZIP"
                    />
                  </div>
                  <div className="sm:col-span-6">
                    <input
                      type="text"
                      value={newRepFirm.website}
                      onChange={e => setNewRepFirm({ ...newRepFirm, website: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Website (optional)"
                    />
                  </div>
                </div>
              </div>

              {/* Commission / Territory */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2 uppercase tracking-wide">Commission & Territory</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      MSRP Adjustment % <span className="text-gray-400">(Kim's hidden margin — default 2%)</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={newRepFirm.price_adjustment_percent}
                      onChange={e => setNewRepFirm({ ...newRepFirm, price_adjustment_percent: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Territory (optional)</label>
                    <select
                      value={newRepFirm.territory_id}
                      onChange={e => setNewRepFirm({ ...newRepFirm, territory_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">— None —</option>
                      {territories.map((t: any) => (
                        <option key={t.id} value={t.id}>
                          {t.name} {Array.isArray(t.states) && t.states.length > 0 ? `(${t.states.join(', ')})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Commission defaults to 10% screens / 5% accessories. To override, edit the firm after creation.
                </p>
              </div>

              {/* Primary portal contact */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2 uppercase tracking-wide">Primary Portal Login (Optional)</h3>
                <p className="text-xs text-gray-500 mb-3">If you enter an email here, we'll create a rep portal login and send a welcome email with a temporary password.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <input
                      type="text"
                      value={newRepFirm.primary_first_name}
                      onChange={e => setNewRepFirm({ ...newRepFirm, primary_first_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="First Name"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={newRepFirm.primary_last_name}
                      onChange={e => setNewRepFirm({ ...newRepFirm, primary_last_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Last Name"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      value={newRepFirm.primary_email}
                      onChange={e => setNewRepFirm({ ...newRepFirm, primary_email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Login Email"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      value={newRepFirm.primary_phone}
                      onChange={e => setNewRepFirm({ ...newRepFirm, primary_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Phone"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
                <textarea
                  value={newRepFirm.contract_notes}
                  onChange={e => setNewRepFirm({ ...newRepFirm, contract_notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Billing CCs, contract terms, special arrangements..."
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setShowAddRepFirm(false)}
                disabled={addRepFirmSaving}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                {addRepFirmSuccess ? 'Close' : 'Cancel'}
              </button>
              {!addRepFirmSuccess && (
                <button
                  onClick={submitAddRepFirm}
                  disabled={addRepFirmSaving || !newRepFirm.name.trim()}
                  className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg disabled:opacity-50"
                >
                  {addRepFirmSaving ? 'Creating…' : 'Create Rep Firm'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// System Banner Tab Component
const BLANK_BANNER = () => ({
  active: true,
  message: '',
  type: 'info',
  color: '#3b82f6',
  text_color: '#ffffff',
  dismissible: true,
  id: `banner_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  expires_at: '',
  audiences: ['dealer'],
});

function SystemBannerTab() {
  const [banners, setBanners] = useState<any[]>([]);
  const [editingIdx, setEditingIdx] = useState<number>(-1);
  const [banner, setBanner] = useState<any>(BLANK_BANNER());

  const AUDIENCE_OPTIONS: Array<{ id: string; label: string; group: 'external' | 'internal' }> = [
    { id: 'dealer', label: 'Dealer Portal', group: 'external' },
    { id: 'rep', label: 'Rep Firm Portal', group: 'external' },
    { id: 'workstation', label: 'Workstation PWA (factory floor)', group: 'internal' },
    { id: 'factory', label: 'Factory Admin', group: 'internal' },
    { id: 'shipping', label: 'Shipping Portal', group: 'internal' },
    { id: 'sales', label: 'Sales Portal', group: 'internal' },
    { id: 'maintenance', label: 'Maintenance Portal', group: 'internal' },
  ];

  const toggleAudience = (id: string) => {
    const current: string[] = Array.isArray(banner.audiences) ? banner.audiences : [];
    const next = current.includes(id) ? current.filter(a => a !== id) : [...current, id];
    setBanner({ ...banner, audiences: next });
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const TYPE_PRESETS: Record<string, { color: string; text_color: string; label: string }> = {
    info: { color: '#3b82f6', text_color: '#ffffff', label: 'Info (Blue)' },
    warning: { color: '#f59e0b', text_color: '#000000', label: 'Warning (Amber)' },
    success: { color: '#10b981', text_color: '#ffffff', label: 'Success (Green)' },
    danger: { color: '#ef4444', text_color: '#ffffff', label: 'Danger (Red)' },
    promo: { color: '#8b5cf6', text_color: '#ffffff', label: 'Promo (Purple)' },
    custom: { color: banner.color, text_color: banner.text_color, label: 'Custom' },
  };

  useEffect(() => { loadBanners(); }, []);

  const loadBanners = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/system-settings/banners');
      setBanners(Array.isArray(data) ? data : []);
    } catch {
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  /** Persist the full array. Caller passes the new array. */
  const persistBanners = async (next: any[]) => {
    setSaving(true);
    setError('');
    try {
      await apiRequest('/system-settings/banners', {
        method: 'POST',
        body: JSON.stringify({ banners: next }),
      });
      setBanners(next);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const startNew = () => {
    setBanner(BLANK_BANNER());
    setEditingIdx(banners.length); // append slot
    setSuccess('');
    setError('');
  };

  const startEdit = (idx: number) => {
    setBanner({ ...banners[idx] });
    setEditingIdx(idx);
    setSuccess('');
    setError('');
  };

  const cancelEdit = () => {
    setBanner(BLANK_BANNER());
    setEditingIdx(-1);
  };

  const saveCurrent = async () => {
    if (!banner.message?.trim()) {
      setError('Message is required');
      return;
    }
    if (!Array.isArray(banner.audiences) || banner.audiences.length === 0) {
      setError('Pick at least one audience');
      return;
    }
    const next = [...banners];
    if (editingIdx >= 0 && editingIdx < next.length) {
      next[editingIdx] = banner;
    } else {
      next.push(banner);
    }
    await persistBanners(next);
    setSuccess(banner.active ? 'Banner saved + published.' : 'Banner saved (inactive).');
    setBanner(BLANK_BANNER());
    setEditingIdx(-1);
  };

  const deleteBanner = async (idx: number) => {
    if (!confirm('Delete this banner? Dealers/workers seeing it now will stop seeing it within 5 min.')) return;
    const next = banners.filter((_, i) => i !== idx);
    await persistBanners(next);
    setSuccess('Banner deleted.');
    if (editingIdx === idx) cancelEdit();
  };

  const toggleActive = async (idx: number) => {
    const next = banners.map((b, i) => i === idx ? { ...b, active: !b.active } : b);
    await persistBanners(next);
    setSuccess(next[idx].active ? 'Banner published.' : 'Banner unpublished.');
  };

  if (loading) return <Card><div className="flex justify-center py-8"><LoadingSpinner /></div></Card>;

  const isEditing = editingIdx >= 0;

  return (
    <Card>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">System Banners</h2>
          <p className="text-sm text-gray-500">Multiple banners stack vertically at the top of any portal you target. Each can have its own audience + expiry.</p>
        </div>
        {!isEditing && (
          <button
            onClick={startNew}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
          >
            + New banner
          </button>
        )}
      </div>

      {error && <div className="my-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
      {success && <div className="my-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{success}</div>}

      {/* Existing banner list (hidden while editing one) */}
      {!isEditing && (
        <div className="mt-4 space-y-2">
          {banners.length === 0 ? (
            <div className="p-6 text-center text-gray-400 border border-dashed rounded-lg">No banners yet. Click "+ New banner" to create one.</div>
          ) : banners.map((b, idx) => {
            const expired = b.expires_at && new Date(b.expires_at).getTime() < Date.now();
            return (
              <div key={b.id || idx} className="flex items-center gap-3 border rounded-lg p-3 bg-gray-50">
                <div className="w-2 h-12 rounded" style={{ background: b.color || '#cbd5e1' }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{b.message || '(empty)'}</div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-1 mt-1">
                    <span>{(b.audiences || []).join(', ') || 'no audience'}</span>
                    {b.expires_at && (
                      <span className={expired ? 'text-red-500' : ''}>
                        {expired ? 'Expired' : `Expires ${new Date(b.expires_at).toLocaleDateString()}`}
                      </span>
                    )}
                    {!b.dismissible && <span>not dismissible</span>}
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded font-medium ${b.active && !expired ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                  {b.active && !expired ? 'Live' : b.active ? 'Expired' : 'Inactive'}
                </span>
                <button onClick={() => toggleActive(idx)} className="text-xs text-gray-600 hover:text-gray-900 underline" disabled={saving}>
                  {b.active ? 'Unpublish' : 'Publish'}
                </button>
                <button onClick={() => startEdit(idx)} className="text-xs text-blue-600 hover:text-blue-800 underline">Edit</button>
                <button onClick={() => deleteBanner(idx)} className="text-xs text-red-600 hover:text-red-800 underline" disabled={saving}>Delete</button>
              </div>
            );
          })}
        </div>
      )}

      {!isEditing && <hr className="my-4" />}
      {!isEditing && <p className="text-xs text-gray-500">Pick "+ New banner" or click "Edit" on a row above to open the editor below.</p>}

      {/* Editor — only renders when adding or editing */}
      {isEditing && (
      <>
      <div className="flex items-center justify-between mt-2 mb-4">
        <h3 className="text-base font-semibold text-gray-900">{editingIdx < banners.length ? `Edit banner` : 'New banner'}</h3>
        <button onClick={cancelEdit} className="text-sm text-gray-500 hover:text-gray-700 underline">Cancel</button>
      </div>

      {/* Preview */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
        <div
          className="px-4 py-3 rounded-lg text-center"
          style={{ backgroundColor: banner.color, color: banner.text_color }}
        >
          <p className="text-sm font-medium">{banner.message || 'Your banner message here...'}</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
          <input
            type="text"
            value={banner.message}
            onChange={(e) => setBanner({ ...banner, message: e.target.value })}
            placeholder="Enter banner message..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Type Presets */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Style</label>
          <div className="flex flex-wrap gap-2">
            {Object.entries(TYPE_PRESETS).map(([key, preset]) => (
              <button
                key={key}
                onClick={() => setBanner({ ...banner, type: key, color: preset.color, text_color: preset.text_color })}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                  banner.type === key ? 'ring-2 ring-blue-500 ring-offset-1' : 'hover:bg-gray-50'
                )}
                style={key !== 'custom' ? { backgroundColor: preset.color + '20', color: preset.color, borderColor: preset.color + '40' } : {}}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Colors */}
        {banner.type === 'custom' && (
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Background</label>
              <input type="color" value={banner.color} onChange={(e) => setBanner({ ...banner, color: e.target.value })} className="h-10 w-16 cursor-pointer" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Text</label>
              <input type="color" value={banner.text_color} onChange={(e) => setBanner({ ...banner, text_color: e.target.value })} className="h-10 w-16 cursor-pointer" />
            </div>
          </div>
        )}

        {/* Dismissible */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={banner.dismissible}
            onChange={(e) => setBanner({ ...banner, dismissible: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Allow dealers to dismiss this banner</span>
        </label>

        {/* Audiences */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Show this banner in</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {AUDIENCE_OPTIONS.map(opt => {
              const checked = Array.isArray(banner.audiences) && banner.audiences.includes(opt.id);
              return (
                <label key={opt.id} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAudience(opt.id)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${opt.group === 'external' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {opt.group}
                  </span>
                </label>
              );
            })}
          </div>
          {(!Array.isArray(banner.audiences) || banner.audiences.length === 0) && (
            <p className="text-xs text-red-600 mt-2">⚠ No audiences selected — banner won't display anywhere.</p>
          )}
        </div>

        {/* Expires at */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Auto-expire date (optional)</label>
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={banner.expires_at ? String(banner.expires_at).split('T')[0] : ''}
              onChange={(e) => setBanner({
                ...banner,
                // Store as end-of-day ISO so the banner stays up through the whole calendar day in admin's tz.
                expires_at: e.target.value ? new Date(`${e.target.value}T23:59:59`).toISOString() : null,
              })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() + 30);
                d.setHours(23, 59, 59, 999);
                setBanner({ ...banner, expires_at: d.toISOString() });
              }}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              + 30 days
            </button>
            {banner.expires_at && (
              <button
                type="button"
                onClick={() => setBanner({ ...banner, expires_at: null })}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Clear
              </button>
            )}
            {banner.expires_at && (
              <span className="text-xs text-gray-500">
                {(() => {
                  const days = Math.ceil((new Date(banner.expires_at).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
                  if (days < 0) return 'Expired';
                  if (days === 0) return 'Expires today';
                  return `Expires in ${days} day${days === 1 ? '' : 's'}`;
                })()}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">Banner auto-hides at end of day on the chosen date. Leave empty for no auto-expiry.</p>
        </div>

        {/* Active toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={banner.active}
            onChange={(e) => setBanner({ ...banner, active: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Active (publish immediately on save)</span>
        </label>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t">
          <button
            onClick={saveCurrent}
            disabled={saving || !banner.message}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving...' : (editingIdx < banners.length ? 'Save changes' : 'Add banner')}
          </button>
          <button
            onClick={cancelEdit}
            disabled={saving}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
      </>
      )}
    </Card>
  );
}

// ============================================================================
// Promotions tab — CRUD for per-dealer promo codes AND system-wide auto promos
// ============================================================================

interface PromoCode {
  id: string;
  code: string;
  dealer_id: string | null;
  dealer_name?: string;
  discount_type: 'percentage' | 'flat' | null;
  discount_value: string | number | null;
  max_uses: number;
  uses_count: number;
  active: boolean;
  expires_at: string | null;
  used_on_order_id: string | null;
  used_on_order_number: string | null;
  used_at: string | null;
  reason: string | null;
  created_at: string;
}

interface Dealer {
  id: string;
  name?: string;
  companyName?: string;
  company_name?: string;
}

function PromotionsTab() {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [statusFilter, setStatusFilter] = useState<'active' | 'used' | 'expired' | 'all'>('active');
  const [loading, setLoading] = useState(true);
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [showIssue, setShowIssue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Issue-code form state
  const [issueForm, setIssueForm] = useState({
    dealerId: '',
    discountType: 'percentage' as 'percentage' | 'flat',
    discountValue: '',
    maxUses: 1,
    expiresAt: '',
    reason: '',
    code: '',
  });

  const loadCodes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/promotions/codes?status=${statusFilter}`);
      setCodes(data || []);
    } catch (e: any) {
      setMsg({ kind: 'err', text: e.message });
    } finally { setLoading(false); }
  }, [statusFilter]);

  const loadDealers = useCallback(async () => {
    try {
      const data = await apiRequest('/dealers?limit=500');
      const rows = data?.dealers || data?.data || data || [];
      setDealers(rows);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadCodes(); loadDealers(); }, [loadCodes, loadDealers]);

  const handleIssue = async () => {
    if (!issueForm.dealerId) { setMsg({ kind: 'err', text: 'Pick a dealer' }); return; }
    if (!issueForm.discountValue || Number(issueForm.discountValue) <= 0) {
      setMsg({ kind: 'err', text: 'Enter a discount value > 0' }); return;
    }
    setSaving(true);
    try {
      await apiRequest('/promotions/codes', {
        method: 'POST',
        body: JSON.stringify({
          dealerId: issueForm.dealerId,
          discountType: issueForm.discountType,
          discountValue: Number(issueForm.discountValue),
          maxUses: Number(issueForm.maxUses) || 1,
          expiresAt: issueForm.expiresAt || undefined,
          reason: issueForm.reason || undefined,
          code: issueForm.code || undefined,
        }),
      });
      setShowIssue(false);
      setIssueForm({ dealerId: '', discountType: 'percentage', discountValue: '', maxUses: 1, expiresAt: '', reason: '', code: '' });
      setMsg({ kind: 'ok', text: 'Code issued' });
      await loadCodes();
    } catch (e: any) {
      setMsg({ kind: 'err', text: e.message });
    } finally { setSaving(false); }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('Revoke this code? The dealer can no longer use it.')) return;
    try {
      await apiRequest(`/promotions/codes/${id}/revoke`, { method: 'POST' });
      await loadCodes();
    } catch (e: any) {
      setMsg({ kind: 'err', text: e.message });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => setMsg({ kind: 'ok', text: `Copied "${text}"` }),
      () => setMsg({ kind: 'err', text: 'Copy failed' }),
    );
  };

  const codeStatus = (c: PromoCode): { label: string; color: string } => {
    if (!c.active) return { label: 'Revoked', color: 'bg-red-100 text-red-700' };
    if (c.uses_count >= c.max_uses) return { label: 'Used', color: 'bg-gray-200 text-gray-700' };
    if (c.expires_at && new Date(c.expires_at) < new Date()) return { label: 'Expired', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Active', color: 'bg-green-100 text-green-700' };
  };

  const dealerName = (d: Dealer) => d.companyName || d.company_name || d.name || 'Unknown';

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Per-Dealer Promo Codes</h2>
            <p className="text-sm text-gray-500 mt-1">
              Single-dealer codes with their own expiration and discount config.
              Great for one-off concessions (comp for a late shipment, loyalty
              reward, VIP incentive). Dealer types the code at checkout.
            </p>
          </div>
          <button
            onClick={() => setShowIssue(v => !v)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            {showIssue ? 'Cancel' : '+ Issue Code'}
          </button>
        </div>

        {msg && (
          <div className={clsx('mb-4 px-4 py-2 rounded-lg text-sm', msg.kind === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800')}>
            {msg.text}
            <button onClick={() => setMsg(null)} className="float-right text-current opacity-60">×</button>
          </div>
        )}

        {showIssue && (
          <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
            <h3 className="font-medium text-gray-900">Issue New Code</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Dealer</label>
                <select
                  value={issueForm.dealerId}
                  onChange={e => setIssueForm({ ...issueForm, dealerId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select dealer…</option>
                  {[...dealers].sort((a, b) => dealerName(a).localeCompare(dealerName(b))).map(d => (
                    <option key={d.id} value={d.id}>{dealerName(d)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Discount</label>
                <div className="flex gap-2">
                  <select
                    value={issueForm.discountType}
                    onChange={e => setIssueForm({ ...issueForm, discountType: e.target.value as 'percentage' | 'flat' })}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="percentage">% off</option>
                    <option value="flat">$ off</option>
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={issueForm.discountValue}
                    onChange={e => setIssueForm({ ...issueForm, discountValue: e.target.value })}
                    placeholder={issueForm.discountType === 'percentage' ? '5' : '250'}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Expires</label>
                <input
                  type="datetime-local"
                  value={issueForm.expiresAt}
                  onChange={e => setIssueForm({ ...issueForm, expiresAt: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Max uses</label>
                <input
                  type="number"
                  min="1"
                  value={issueForm.maxUses}
                  onChange={e => setIssueForm({ ...issueForm, maxUses: Number(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Custom code <span className="text-gray-400 font-normal">(optional — auto-generated if blank)</span>
                </label>
                <input
                  type="text"
                  value={issueForm.code}
                  onChange={e => setIssueForm({ ...issueForm, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. VIP24"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Reason <span className="text-gray-400 font-normal">(internal note)</span>
                </label>
                <input
                  type="text"
                  value={issueForm.reason}
                  onChange={e => setIssueForm({ ...issueForm, reason: e.target.value })}
                  placeholder="e.g. Compensation for delayed shipment on VT26050033"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowIssue(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleIssue}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Issuing…' : 'Issue Code'}
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">Filter:</span>
          {(['active', 'used', 'expired', 'all'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={clsx(
                'px-3 py-1 rounded-full text-xs font-medium',
                statusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
              )}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-8"><LoadingSpinner /></div>
        ) : codes.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">No codes yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Dealer</th>
                  <th className="py-2 pr-3">Discount</th>
                  <th className="py-2 pr-3">Uses</th>
                  <th className="py-2 pr-3">Expires</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Reason</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(c => {
                  const st = codeStatus(c);
                  const val = c.discount_value != null ? Number(c.discount_value) : 0;
                  const discountText = c.discount_type === 'flat'
                    ? `$${val.toFixed(2)} off`
                    : `${val.toFixed(2)}%`;
                  return (
                    <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 pr-3">
                        <button
                          onClick={() => copyToClipboard(c.code)}
                          className="font-mono font-semibold text-blue-700 hover:text-blue-900"
                          title="Copy code"
                        >
                          {c.code}
                        </button>
                      </td>
                      <td className="py-2 pr-3 text-gray-800">{c.dealer_name || '—'}</td>
                      <td className="py-2 pr-3">{discountText}</td>
                      <td className="py-2 pr-3 text-gray-600">
                        {c.uses_count} / {c.max_uses}
                        {c.used_on_order_number && (
                          <div className="text-xs text-gray-400">on {c.used_on_order_number}</div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-gray-600 text-xs">
                        {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="py-2 pr-3">
                        <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', st.color)}>
                          {st.label}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs text-gray-500 max-w-xs truncate" title={c.reason || ''}>
                        {c.reason || '—'}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {c.active && c.uses_count < c.max_uses && (
                          <button
                            onClick={() => handleRevoke(c.id)}
                            className="text-red-600 hover:text-red-800 text-xs font-medium"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-gray-900">System-Wide Sales</h2>
        <p className="text-sm text-gray-500 mt-1">
          Time-windowed auto-promos (e.g. "4% off orders placed this weekend").
          Apply to every eligible dealer automatically during the date range.
          No code required.
        </p>
        <div className="mt-4 p-6 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
          System-wide CRUD UI coming — the pieces are wired end-to-end on the
          API (item 2 service + item 10 auto-banner). For now, use the
          endpoint <code className="text-gray-700">POST /promotions</code>
          directly, or the campaign broadcaster below.
        </div>
      </Card>

      <SocialCopyGenerator />
    </div>
  );
}

// ============================================================================
// Social copy generator — turns a promo brief into Facebook / Instagram /
// LinkedIn text blocks. Client-side only, no API posting.
// ============================================================================

function SocialCopyGenerator() {
  const [form, setForm] = useState({
    headline: '',
    discountType: 'percentage' as 'percentage' | 'flat',
    discountValue: '',
    endsAt: '',
    promoCode: '',
    customPitch: '',
  });
  const [copied, setCopied] = useState<string | null>(null);

  const discountLabel = () => {
    const v = Number(form.discountValue);
    if (!v || isNaN(v)) return '';
    return form.discountType === 'percentage' ? `${v}% OFF` : `$${v} OFF`;
  };

  const endsLabel = () => {
    if (!form.endsAt) return '';
    try {
      const d = new Date(form.endsAt);
      return d.toLocaleString('en-US', {
        weekday: 'short', month: 'long', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
        timeZone: 'America/Chicago', timeZoneName: 'short',
      });
    } catch { return form.endsAt; }
  };

  const basePitch = () => form.customPitch.trim() || (
    form.discountType === 'percentage'
      ? `Save on every retractable screen order this window — the ${form.discountValue}% is automatic at checkout.`
      : `Every order this window gets $${form.discountValue} knocked off — automatic at checkout.`
  );

  const codeLine = () => form.promoCode ? `\n\nUse code: ${form.promoCode.toUpperCase()}` : '';

  const facebookText = `${form.headline || 'Limited-Time Sale'} — ${discountLabel()}\n\n${basePitch()}${codeLine()}\n\n${form.endsAt ? `Ends ${endsLabel()}.` : ''}\n\nBuilt in Texas. Retractable exterior screens for patios, porches, garages. Talk to a Keder Screens dealer to get started.\n\n👉 https://kederscreens.com`;

  const instagramText = `${form.headline || 'Limited-time sale'} 🔥\n${discountLabel()} — automatic${form.promoCode ? ` with code ${form.promoCode.toUpperCase()}` : ''}${form.endsAt ? `\nEnds ${endsLabel()}` : ''}\n\n${basePitch()}\n\nLink in bio. #KederScreens #RetractableScreens #OutdoorLiving #PatioScreens #ShadeSolutions #MadeInTexas`;

  const linkedInText = `${form.headline || 'Limited-Time Dealer Program Offer'}\n\n${basePitch()}${codeLine()}\n\n${form.endsAt ? `Offer ends ${endsLabel()}. ` : ''}Reach out to your Keder Screens rep, or start an order in the dealer portal.\n\nKeder Screens — retractable exterior shading, engineered and manufactured in Texas.`;

  const copy = (kind: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(kind);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const isFilledIn = form.headline || form.discountValue;

  return (
    <Card>
      <h2 className="text-lg font-semibold text-gray-900">Social Media Copy</h2>
      <p className="text-sm text-gray-500 mt-1 mb-4">
        Fill in the promo details and get three ready-to-post captions
        (Facebook, Instagram, LinkedIn). Copy each block to your clipboard
        and paste into the platform.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Headline / Sale Name</label>
          <input
            type="text"
            value={form.headline}
            onChange={e => setForm({ ...form, headline: e.target.value })}
            placeholder="e.g. 4th of July Weekend Sale"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Discount</label>
          <div className="flex gap-2">
            <select
              value={form.discountType}
              onChange={e => setForm({ ...form, discountType: e.target.value as 'percentage' | 'flat' })}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="percentage">% off</option>
              <option value="flat">$ off</option>
            </select>
            <input
              type="number"
              step="0.01"
              value={form.discountValue}
              onChange={e => setForm({ ...form, discountValue: e.target.value })}
              placeholder={form.discountType === 'percentage' ? '4' : '250'}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Ends</label>
          <input
            type="datetime-local"
            value={form.endsAt}
            onChange={e => setForm({ ...form, endsAt: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Promo Code <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={form.promoCode}
            onChange={e => setForm({ ...form, promoCode: e.target.value.toUpperCase() })}
            placeholder="e.g. SUMMER10"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Custom pitch line <span className="text-gray-400 font-normal">(optional — auto-generated if blank)</span>
          </label>
          <textarea
            value={form.customPitch}
            onChange={e => setForm({ ...form, customPitch: e.target.value })}
            placeholder="Write your own hook here…"
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
      </div>

      {isFilledIn ? (
        <div className="space-y-3">
          {[
            { kind: 'facebook', label: 'Facebook', color: 'bg-blue-600', text: facebookText, hint: 'Longer form. Paste as a post to your business page.' },
            { kind: 'instagram', label: 'Instagram', color: 'bg-pink-600', text: instagramText, hint: 'Short + hashtags. Also works for IG Stories caption.' },
            { kind: 'linkedin', label: 'LinkedIn', color: 'bg-sky-700', text: linkedInText, hint: 'Professional tone. Post to Keder Screens company page.' },
          ].map(({ kind, label, color, text, hint }) => (
            <div key={kind} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <span className={`inline-block w-2 h-2 rounded-full ${color}`} />
                  <span className="font-semibold text-sm text-gray-800">{label}</span>
                  <span className="text-xs text-gray-500">{hint}</span>
                </div>
                <button
                  onClick={() => copy(kind, text)}
                  className="px-3 py-1 bg-white border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-100"
                >
                  {copied === kind ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <pre className="px-4 py-3 text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                {text}
              </pre>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-6 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
          Add a headline or discount to see the generated posts.
        </div>
      )}
    </Card>
  );
}

function SmsNotificationToggle() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    apiRequest('/system-settings/sms-notifications')
      .then((r: any) => setEnabled(!!r?.enabled))
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    try {
      await apiRequest('/system-settings/sms-notifications', {
        method: 'POST',
        body: JSON.stringify({ enabled: next }),
      });
      setEnabled(next);
      setMsg('Saved');
      setTimeout(() => setMsg(''), 2000);
    } catch (e: any) {
      alert(e.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-2">Transactional Order SMS</h2>
      <p className="text-sm text-gray-500 mb-4">
        Master switch for automated order texts (ready / balance / shipped / delivered). Each dealer
        must also opt in individually on their detail page. Requires 10DLC registration in Brevo to
        actually deliver.
      </p>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <label className="flex items-center gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={enabled} disabled={saving} onChange={toggle} />
          <span>{enabled ? 'Order SMS notifications are ON' : 'Order SMS notifications are OFF'}</span>
          {msg && <span className="text-green-600 text-xs">{msg}</span>}
        </label>
      )}
    </Card>
  );
}

export default SettingsPage;
